import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getComissionsQuery } from "../../queries";
import TableContainer from "../Common/TableContainer";
import DeleteModal from "../Common/DeleteModal";
import {
  Row,
  Modal,
  Form,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createPaymentComission, deletePaymentComission } from "../../api/user";
import { useTranslation } from "react-i18next";

export function DefaultComissions() {
  const [customFields, setCustomFields] = useState([]);
  const [modal, setModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: comissionsData } = useQuery({
    ...getComissionsQuery(),
  });

  const toggle = () => {
    setModal(!modal);
    setCustomFields([]);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      defaultCommission: 0,
      paymentSystemCommissions: {},
    },
    validationSchema: Yup.object({
      defaultCommission: Yup.number()
        .required("Please enter commission")
        .min(0, "Must be a positive number"),
    }),
    onSubmit: (values, { resetForm }) => {
      createPaymentComission({
        defaultCommission: values.defaultCommission,
        paymentSystemCommissions: customFields.reduce((obj, item) => {
          obj[item.key] = isNaN(item.value) ? item.value : Number(item.value);
          return obj;
        }, {}),
      })
        .then(() => {
          queryClient.invalidateQueries({
            queryKey: ["comissions"],
          });
          resetForm();
          setCustomFields([]);
          toggle();
        })
        .catch((err) => {
          if (err?.data.error?.message) {
            alert(err?.data.error?.message);
          }
        });
    },
  });

  const addCustomField = () => {
    setCustomFields([...customFields, { key: "", value: 0 }]);
  };

  const updateCustomField = (index, key, value) => {
    const updatedFields = [...customFields];
    updatedFields[index][key] = value;
    setCustomFields(updatedFields);
  };

  const removeCustomField = (index) => {
    const updatedFields = customFields.filter((_, i) => i !== index);
    setCustomFields(updatedFields);
  };

  const columns = useMemo(
    () => [
      {
        header: t("Comission"),
        accessorKey: "defaultCommission",
        enableColumnFilter: false,
      },
      {
        header: t("Additional"),
        cell: ({ row }) =>
          Object.entries(row.original.paymentSystemCommissions)
            .map(([key, value]) => `${key}: ${value}%`)
            .join(", "),
        enableColumnFilter: false,
      },
      {
        header: t("Actions"),
        cell: ({ row }) => (
          <>
            {row.original.isActive === false && !row.original.activeTo && (
              <button
                className="btn btn-sm btn-danger"
                style={{ marginLeft: "10px" }}
                onClick={() => setDeleteModal(row.original.id)}
              >
                <i className="ri-delete-bin-6-fill"></i>
              </button>
            )}
          </>
        ),
      },
    ],
    []
  );

  const onDelete = () => {
    deletePaymentComission(deleteModal)
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: ["comissions"],
        });
      })
      .catch((err) => {
        if (err?.data.error?.message) {
          alert(err?.data.error?.message);
        }
      })
      .finally(() => {
        setDeleteModal(false);
      });
  };

  return (
    <>
      <div className="card-body pt-0">
        <DeleteModal
          show={deleteModal}
          onDeleteClick={onDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <div>
          <Row className="g-4 mb-3 align-items-center">
            <div className="col-sm">
              <div>
                <h5 className="card-title mb-0">{t("Comissions")}</h5>
              </div>
            </div>
            <div className="col-sm-auto">
              <div>
                <button
                  type="button"
                  className="btn btn-success add-btn"
                  id="create-btn"
                  onClick={() => setModal(true)}
                >
                  <i className="ri-add-line align-bottom me-1"></i>{" "}
                  {t("create")}
                </button>
              </div>
            </div>
          </Row>
          <TableContainer
            columns={columns}
            data={comissionsData?.data?.data || []}
            isAddUserList={false}
            customPageSize={10}
            className="custom-header-css"
          />
        </div>
        <Modal isOpen={modal} toggle={toggle} centered>
          <ModalHeader className="bg-light p-3" toggle={toggle}>
            {t("create")}
          </ModalHeader>
          <Form onSubmit={validation.handleSubmit}>
            <ModalBody>
              <Label>{t("Comission percent")}</Label>
              <Input
                name="defaultCommission"
                type="number"
                placeholder={t("Comission percent")}
                onChange={validation.handleChange}
                onBlur={validation.handleBlur}
                value={validation.values.defaultCommission}
                invalid={
                  validation.touched.defaultCommission &&
                  validation.errors.defaultCommission
                    ? true
                    : false
                }
              />
              <FormFeedback>{validation.errors.defaultCommission}</FormFeedback>
              <h6 className="mt-3">{t("Additional Commissions")}</h6>
              {customFields.map((field, index) => (
                <div key={index} className="d-flex align-items-center mb-2">
                  <Input
                    type="text"
                    placeholder="Key"
                    value={field.key}
                    onChange={(e) =>
                      updateCustomField(index, "key", e.target.value)
                    }
                    className="me-2"
                  />
                  <Input
                    type="number"
                    placeholder="Value %"
                    value={field.value}
                    onChange={(e) =>
                      updateCustomField(index, "value", e.target.value)
                    }
                  />
                  <button
                    type="button"
                    className="btn btn-danger btn-sm ms-2"
                    onClick={() => removeCustomField(index)}
                  >
                    X
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="btn btn-info btn-sm mt-2"
                onClick={addCustomField}
              >
                + {t("Add Field")}
              </button>
            </ModalBody>
            <ModalFooter>
              <button type="button" className="btn btn-light" onClick={toggle}>
                {t("Close")}
              </button>
              <button type="submit" className="btn btn-success">
                {t("create")}
              </button>
            </ModalFooter>
          </Form>
        </Modal>
        <ToastContainer closeButton={false} limit={1} />
      </div>
    </>
  );
}
